import { cx } from 'emotion';
import React, {
  useRef, useEffect, useContext, CSSProperties,
} from 'react';

import { usePreloadImage } from '@ezugi/hooks';
import { DeviceInfo } from '@ezugi/utils';

import {
  headerClassName,
  imageClassName,
  infoTextMessageClassName,
  poweredByClassName,
  fullScreenHeaderStyles,
  fullScreenLoader,
  preloaderHeightClassName,
  infoTextMessageMobile,
  poweredByImgClassName,
  headerMobileClassName,
} from '../style.css';
import { PreloaderContext } from './PreloaderContext';
import { PreloaderSpinner } from './PreloaderSpinner';

export type PreloaderHeaderProps = {
  children?: React.ReactNode;
  imageComponent?: React.FC<{ style?: CSSProperties; className?: string }>;
  image?: string;
  showInfoMessage?: boolean;
  infoText: string;
  poweredBy?: {
    label?: string;
    logo?: string;
  };
}

const BaseImage = ({ className, ...props }: any) => (
  <div className={cx(imageClassName, className)} {...props} />
);

export const PreloaderHeader: React.FC<PreloaderHeaderProps> = ({
  children,
  imageComponent,
  image,
  showInfoMessage = false,
  infoText,
  poweredBy = {},
}) => {
  const { show } = useContext(PreloaderContext);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const { fullScreenImage, showFooter, onDestroy } = useContext(PreloaderContext);

  const preloadPoweredByLogo = usePreloadImage({ src: poweredBy?.logo });
  const preloadBackground = usePreloadImage({ src: image });

  const showPowerByLogo = Boolean(!!poweredBy && preloadPoweredByLogo.loaded);
  const Image = imageComponent ?? BaseImage;

  useEffect(() => {
    const currentElement = headerRef.current;

    const handleOnDestroy = (e: TransitionEvent) => {
      if (e.target === currentElement && e.propertyName === 'transform') {
        onDestroy(e);
      }
    };

    currentElement?.addEventListener('transitionend', handleOnDestroy);
    return () => currentElement?.removeEventListener('transitionend', handleOnDestroy);
  }, [ onDestroy ]);

  return (
    <div
      ref={headerRef}
      data-e2e="preloader-header"
      className={cx(headerClassName[show ? 'visible' : 'hidden'], {
        [fullScreenHeaderStyles]: fullScreenImage && !DeviceInfo.isHandheld,
        [headerMobileClassName]: DeviceInfo.isHandheld,
      })}
    >
      <Image
        data-e2e="preloader-background"
        className={cx({
          [preloaderHeightClassName]: DeviceInfo.isHandheld,
        })}
        style={{
          ...(preloadBackground.loaded && {
            backgroundImage: `url(${image})`,
          }),
        }}
      />
      {children}
      {!showFooter && <PreloaderSpinner className={fullScreenLoader} />}
      {showInfoMessage && (
        <p
          dangerouslySetInnerHTML={{ __html: infoText }}
          className={cx(infoTextMessageClassName, {
            [infoTextMessageMobile]: DeviceInfo.isHandheld,
          })}
        />
      )}
      {showPowerByLogo && (
        <div className={poweredByClassName} data-e2e="powered-by-logo">
          {poweredBy.label}
          <img alt="ezugi logo" src={poweredBy.logo} className={poweredByImgClassName} />
        </div>
      )}
    </div>
  );
};
